import { home, rdCro, regulatory } from "@/content/site";
import { ButtonLink } from "@/components/common/ButtonLink";
import usePageMeta from "@/hooks/usePageMeta";
import { Hero } from "@/components/sections/Hero";
import { Marquee } from "@/components/sections/Marquee";
import { ServiceCards } from "@/components/sections/ServiceCards";
import { FocusBento } from "@/components/sections/FocusBento";
import { WorldMap } from "@/components/sections/WorldMap";
import { CredibilityStrip } from "@/components/sections/CredibilityStrip";
import { CtaBand } from "@/components/sections/CtaBand";
import { RegulatoryNote } from "@/components/sections/RegulatoryNote";
import { MarketEntry } from "@/components/sections/MarketEntry";
import { LatestNews } from "@/components/sections/LatestNews";
import { PartnerStrip } from "@/components/sections/PartnerStrip";
import { SectionHeader, noStop } from "@/components/common/SectionHeader";
import { Reveal } from "@/components/motion/Reveal";

export default function Home() {
  usePageMeta(home.meta);
  return (
    <>
      <Hero />
      <section className="pb-16 sm:pb-20" data-testid="home-stats">
        <div className="container-x">
          <CredibilityStrip stats={home.stats} />
        </div>
      </section>
      <Marquee items={home.marquee} />

      <section className="section-y" data-testid="home-intro">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <p className="eyebrow flex items-center gap-3 lg:col-span-3"><span className="cross" />{home.intro.eyebrow}</p>
          <Reveal className="lg:col-span-9">
            <p className="font-display text-[1.6rem] font-medium leading-[1.25] tracking-[-0.03em] text-ink sm:text-4xl lg:text-[2.6rem]">{home.intro.text}</p>
          </Reveal>
        </div>
      </section>

      <PartnerStrip />

      <section className="pb-24 sm:pb-32" data-testid="home-services">
        <div className="container-x">
          <SectionHeader eyebrow="Services" index="01" title="Three ways SPC moves products forward" className="mb-14" />
          <ServiceCards services={home.services} />
        </div>
      </section>

      <section className="pb-24 sm:pb-32" data-testid="home-api-sourcing">
        <div className="container-x grid gap-8 border-t border-line pt-14 lg:grid-cols-12 lg:gap-10">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow mb-6 flex items-center gap-3"><span className="cross" /><span className="text-muted">02 /</span>{regulatory.eyebrow}</p>
            <h2 className="h-section">{noStop(regulatory.title)}</h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 lg:pt-10">
            <RegulatoryNote testId="home-regulatory-note" />
          </Reveal>
        </div>
      </section>

      <MarketEntry index="03" />

      <section className="pb-24 sm:pb-32" data-testid="home-rdcro-teaser">
        <div className="container-x">
          <Reveal className="spec-corners grid gap-8 border border-line bg-surface p-8 sm:p-12 lg:grid-cols-12 lg:items-end lg:gap-10">
            <div className="lg:col-span-8">
              <p className="eyebrow mb-6 flex items-center gap-3"><span className="cross" />{rdCro.teaser.eyebrow}</p>
              <h2 className="h-section">{noStop(rdCro.teaser.title)}</h2>
              <p className="lead mt-6 max-w-2xl">{rdCro.hero.body}</p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <ButtonLink to="/rd-cro-services" variant="accent" testId="home-rdcro-learn-more">
                {rdCro.teaser.button}
              </ButtonLink>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-y border-t border-line" data-testid="home-focus">
        <div className="container-x">
          <SectionHeader eyebrow={home.focus.eyebrow} index="04" title={home.focus.title} className="mb-14" />
          <FocusBento areas={home.focus.areas} />
        </div>
      </section>

      <section className="section-y border-t border-line" data-testid="home-map">
        <div className="container-x">
          <SectionHeader eyebrow={home.map.eyebrow} index="05" title={home.map.title} body={home.map.body} className="mb-14" />
          <WorldMap />
        </div>
      </section>

      <LatestNews />

      <CtaBand />
    </>
  );
}
